/**
 * Poll service - Trip collaboration polls
 * Integrates with backend /trip API (see specs/006-/contracts/poll-api.md)
 */

import { apiDelete, apiGet, apiPost } from './apiClient';
import type {
  CreatePollRequest,
  CreatePollResponse,
  Poll,
  PollDetailResponse,
  PollListResponse,
  PollSummary,
} from '../types/poll';

/**
 * API error that preserves the backend exitCode (same convention as
 * memberService's MemberApiError), so callers can branch on poll-specific
 * error codes (70001-70005, see contracts/poll-api.md).
 */
export class PollApiError extends Error {
  constructor(public code: number, message: string) {
    super(message);
    this.name = 'PollApiError';
  }
}

export async function createPoll(tripId: string, input: CreatePollRequest): Promise<string> {
  const response = await apiPost<CreatePollResponse>(`/trip/${tripId}/polls`, input);

  if (response.exitCode !== 1 || response.data === undefined) {
    throw new PollApiError(response.exitCode, response.message || '建立投票失敗');
  }

  return response.data;
}

export async function getPolls(tripId: string): Promise<PollSummary[]> {
  const response = await apiGet<PollListResponse>(`/trip/${tripId}/polls`);

  if (response.exitCode !== 1) {
    throw new PollApiError(response.exitCode, response.message || '取得投票列表失敗');
  }

  return response.data;
}

export async function getPollDetail(tripId: string, pollId: string): Promise<Poll> {
  const response = await apiGet<PollDetailResponse>(`/trip/${tripId}/polls/${pollId}`);

  if (response.exitCode !== 1 || !response.data) {
    throw new PollApiError(response.exitCode, response.message || '取得投票詳情失敗');
  }

  return response.data;
}

export async function vote(tripId: string, pollId: string, optionId: string): Promise<void> {
  const response = await apiPost<{ exitCode: number; message: string }>(
    `/trip/${tripId}/polls/${pollId}/vote`,
    { optionId }
  );

  if (response.exitCode !== 1) {
    throw new PollApiError(response.exitCode, response.message || '投票失敗');
  }
}

export async function revokeVote(tripId: string, pollId: string, optionId: string): Promise<void> {
  const response = await apiDelete<{ exitCode: number; message: string }>(
    `/trip/${tripId}/polls/${pollId}/vote?optionId=${optionId}`
  );

  if (response.exitCode !== 1) {
    throw new PollApiError(response.exitCode, response.message || '收回投票失敗');
  }
}

export async function closePoll(tripId: string, pollId: string): Promise<void> {
  const response = await apiPost<{ exitCode: number; message: string }>(
    `/trip/${tripId}/polls/${pollId}/close`
  );

  if (response.exitCode !== 1) {
    throw new PollApiError(response.exitCode, response.message || '結束投票失敗');
  }
}

export const pollService = {
  createPoll,
  getPolls,
  getPollDetail,
  vote,
  revokeVote,
  closePoll,
};
